// ==========================
// Version 7 — ConfirmDialog.js
// - Adds aria-labelledby + aria-describedby
// - Adds focus trap + restores focus to opener on close
// - Keeps brand styles and Esc/backdrop close
// ==========================
import React from 'react';

const NAVY = '#0A1A33';
const GOLD = '#C9A44C';
const IVORY = '#FDFCF9';
const WARM_GRAY = '#888888';
const ACCESSIBLE_GOLD = '#8A6D2E';
const BURGUNDY = '#5C1A2B';

export default function ConfirmDialog({
  open,
  title = 'Are you sure?',
  message = 'This action cannot be undone.',
  confirmText = 'Delete',
  cancelText = 'Cancel',
  danger = false,
  loading = false,
  onConfirm,
  onCancel,
}) {
  const titleId = React.useId();
  const descId = React.useId();
  const firstRef = React.useRef(null);
  const prevFocusRef = React.useRef(null);

  // Hook must be called unconditionally; guard inside.
  React.useEffect(() => {
    if (!open) return;
    prevFocusRef.current = document.activeElement;

    const container = document.getElementById(`cd-${titleId}`)?.closest('[role="dialog"]') || null;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') onCancel?.();
      if (e.key !== 'Tab') return;

      const focusEls = container
        ? Array.from(container.querySelectorAll('a[href],button:not([disabled]),textarea,input,select,[tabindex]:not([tabindex="-1"])'))
        : [];
      if (!focusEls.length) return;
      const first = focusEls[0];
      const last = focusEls[focusEls.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault(); first.focus();
      }
    };

    setTimeout(() => firstRef.current?.focus(), 0); // initial focus
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      try { prevFocusRef.current?.focus?.(); } catch {}
    };
  }, [open, onCancel, titleId]);

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onCancel?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  if (!open) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !loading) onCancel?.();
  };

  return (
    <div
      style={styles.backdrop}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={descId}
      onMouseDown={handleBackdropClick}
    >
      <div style={styles.modal} onMouseDown={(e) => e.stopPropagation()}>
        <h3 id={`cd-${titleId}`} style={styles.title}>{title}</h3>
        <p id={descId} style={styles.message}>{message}</p>

        <div style={styles.actions}>
          <button
            type="button"
            onClick={onCancel}
            style={styles.btnGhost}
            disabled={loading}
            ref={firstRef}
          >
            {cancelText}
          </button>

          <button
            type="button"
            onClick={onConfirm}
            style={{ ...styles.btnPrimary, ...(danger ? styles.btnDanger : null) }}
            disabled={loading}
          >
            {loading ? (danger ? 'Deleting…' : 'Working…') : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  backdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(10,26,51,0.55)', // Navy-tinted scrim
    display: 'grid',
    placeItems: 'center',
    zIndex: 1000,
    padding: 12,
  },
  modal: {
    width: 'min(92vw, 480px)',
    background: IVORY,
    borderRadius: 16,
    padding: 18,
    boxShadow: '0 20px 60px rgba(10,26,51,0.35)',
    border: `1px solid rgba(201,164,76,0.35)`,
    color: NAVY,
  },
  title: {
    margin: '0 0 6px',
    fontFamily: 'serif',
    fontSize: 20,
    color: NAVY,
  },
  message: { margin: '0 0 18px', color: WARM_GRAY, lineHeight: 1.5 },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: 10 },
  btnGhost: {
    padding: '10px 14px',
    borderRadius: 12,
    border: `1px solid ${GOLD}`,
    background: 'transparent',
    color: ACCESSIBLE_GOLD,
    cursor: 'pointer',
    fontWeight: 800,
  },
  btnPrimary: {
    padding: '10px 14px',
    borderRadius: 12,
    border: 'none',
    background: GOLD,
    color: NAVY,
    cursor: 'pointer',
    fontWeight: 800,
  },
  btnDanger: { background: BURGUNDY, color: IVORY, border: `1px solid ${BURGUNDY}` },
};
// ==========================
// End Version 7 — ConfirmDialog.js
// ==========================
